import { createScoreEngine } from '@velainc/listing-score-engine';
import { Map } from 'immutable';

import { shapeKeywordsForAPI } from './shapeAttributes';
import { tagsToArray } from '../tags';
import { keepOld } from '../iterable/keepOld';
import { convertBodyHTMLToDescription } from './convert';

import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { NEW, VALUE } from '../../constants/product';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';
import { ETSY_AI_CHANNELS } from '../../constants/bulkEdit';

const scoreEngine = createScoreEngine();

function shapeScoresForApp({ channel, scores = {}}) {
  function shapeDetails(components = []) {
    function reduceComponents(result, component) {
      const name = Object.keys(component)[0];
      const value = component[name] === 'complete';
      return result.push(Map({ name, value }));
    }

    return components.reduce(reduceComponents, EMPTY.LIST);
  }

  switch (channel) {
    case ETSY: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        overallScore,
        tagsScore,
        tagsScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
      } = scores;

      return Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
        [ATTRIBUTE.TAGS]: Map({
          details: shapeDetails(tagsScoreComponents.breakdown),
          total: tagsScore,
        }),
      });
    }

    case SHOPIFY: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
        descriptionSEOScore,
        descriptionSEOScoreComponents = {},
        overallScore,
        titleSEOScore,
        titleSEOScoreComponents = {},
      } = scores;

      return Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
        [ATTRIBUTE.PAGE_TITLE]: Map({
          details: shapeDetails(titleSEOScoreComponents.breakdown),
          total: titleSEOScore,
        }),
        [ATTRIBUTE.META_DESCRIPTION]: Map({
          details: shapeDetails(descriptionSEOScoreComponents.breakdown),
          total: descriptionSEOScore,
        }),
      });
    }

    case EBAY: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
        overallScore,
      } = scores;

      return Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
      });
    }

    case FAIRE: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
        overallScore,
      } = scores;

      return Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
      });
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function updateListingScore(product, config = {}) {
  const channel = product.get('channel');

  switch (channel) {
    case ETSY: {
      const scoreChannel = config.titleFormatPreference
        ? ETSY_AI_CHANNELS[config.titleFormatPreference]
        : ETSY;

      const listing = {
        channel: scoreChannel || ETSY,
        description: product.getIn([FORM.DESCRIPTION, VALUE]),
        title: product.getIn([FORM.TITLE, VALUE]),
        tags: tagsToArray(
          product
            .getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP)
            .mergeWith(keepOld, product.getIn([FORM.TAGS, NEW]))
        ),
      };

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores }));
    }

    case SHOPIFY: {
      const listing = {
        channel: SHOPIFY,
        title: product.getIn([FORM.TITLE, VALUE]),
      };

      const {
        all_keywords: allKeywords,
        focus_keyword: focusKeyword,
      } = shapeKeywordsForAPI({ channel, value: product.get('keywords') });

      listing.all_keywords = allKeywords;
      listing.description = product.getIn([FORM.DESCRIPTION, VALUE], '');
      listing.focus_keyword = focusKeyword;
      listing.seo_description = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE], '');
      listing.seo_title = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE], '');

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores }));
    }

    case EBAY: {
      const descriptionHTML = product.getIn([FORM.DESCRIPTION, VALUE], '');
      const descriptionText = convertBodyHTMLToDescription(descriptionHTML);
      
      const listing = {
        channel: EBAY,
        title: product.getIn([FORM.TITLE, VALUE]),
        description: descriptionText,
      };

      const {
        all_keywords: allKeywords,
        focus_keyword: focusKeyword,
      } = shapeKeywordsForAPI({ value: product.get('keywords') });

      listing.all_keywords = allKeywords;
      listing.focus_keyword = focusKeyword;

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores }));
    }

    case FAIRE: {
      const listing = {
        channel: FAIRE,
        title: product.getIn([FORM.TITLE, VALUE]),
        description: product.getIn([FORM.DESCRIPTION, VALUE], ''),
      };

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores }));
    }

    default: {
      return product;
    }
  }
}
